import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CardActionArea
} from '@mui/material';
import {
  Circle as CircleIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { RezdySession } from '../services/rezdyService';
import { getBookingUrl } from '../config/tour-mappings';

interface TourCardProps {
  session: RezdySession;
  onClick?: () => void;
}

/**
 * Get availability status for a session
 * Returns object with status, text, and optional seats count
 */
const getAvailabilityStatus = (session: RezdySession) => {
  const seatsAvailable = session.seatsAvailable;
  
  // Check if seatsAvailable is null, undefined, or negative
  if (seatsAvailable === null || seatsAvailable === undefined || seatsAvailable < 0) {
    return {
      status: 'unavailable' as const,
      text: 'Not Available'
    };
  }
  
  // Check if fully booked (seatsAvailable === 0)
  if (seatsAvailable === 0) {
    return {
      status: 'booked' as const,
      text: 'Fully Booked'
    };
  }
  
  // Available (seatsAvailable > 0)
  return {
    status: 'available' as const,
    text: `${seatsAvailable} seat${seatsAvailable !== 1 ? 's' : ''} available`,
    seats: seatsAvailable
  };
};

const TourCard: React.FC<TourCardProps> = ({ session, onClick }) => {
  const availabilityStatus = getAvailabilityStatus(session);
  const bookingUrl = getBookingUrl(session.productCode);

  // Format the time from startTimeLocal (format: "2025-10-28 08:00:00")
  const formatTime = (timeStr: string) => {
    try {
      const parts = timeStr.split(' ');
      if (parts.length >= 2) {
        return parts[1].substring(0, 5); // Get HH:MM
      }
      return 'TBD';
    } catch {
      return 'TBD';
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    window.open(bookingUrl, '_blank', 'noopener,noreferrer');
  };

  // Determine card styling based on status
  const getCardStyles = () => {
    if (availabilityStatus.status === 'available') {
      return {
        border: '1px solid #e0e0e0',
        backgroundColor: '#ffffff'
      };
    } else if (availabilityStatus.status === 'booked') {
      return {
        border: '1px solid #ffcdd2',
        backgroundColor: '#fafafa'
      };
    } else {
      // unavailable
      return {
        border: '1px solid #bdbdbd',
        backgroundColor: '#f5f5f5',
        opacity: 0.7
      };
    }
  };

  // Determine chip color based on status
  const getChipColor = () => {
    if (availabilityStatus.status === 'available') {
      return 'success' as const;
    } else if (availabilityStatus.status === 'booked') {
      return 'error' as const;
    } else {
      return 'default' as const;
    }
  };

  // Determine indicator color based on status
  const getIndicatorColor = () => {
    if (availabilityStatus.status === 'available') {
      return '#4caf50';
    } else if (availabilityStatus.status === 'booked') {
      return '#f44336';
    } else {
      return '#9e9e9e';
    }
  };

  return (
    <Card 
      sx={{ 
        mb: 2,
        ...getCardStyles(),
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardActionArea onClick={handleClick}>
        <CardContent>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box flex={1}>
              <Box display="flex" alignItems="center" mb={1}>
                <CircleIcon
                  sx={{
                    fontSize: 12,
                    color: getIndicatorColor(),
                    mr: 1
                  }}
                />
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  {session.productName || 'Unknown Tour'}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {formatTime(session.startTimeLocal)}
                {session.totalPrice && session.totalCurrency && (
                  <> • {session.totalCurrency} ${session.totalPrice}</>
                )}
              </Typography>

              <Box mt={1}>
                <Chip
                  label={availabilityStatus.text}
                  size="small"
                  color={getChipColor()}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            <Box>
              <OpenInNewIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default TourCard;


